/**
 * Turns a game's liquidity positions into what omodds renders: the three-market board, and the
 * full game (every market, game and player props). Pure and framework-free so it can be
 * unit-tested — every decision about prices lives here, none in the UI.
 *
 * Everything is built from one primitive, the BOOK: one bettable proposition with its sides
 * (Brewers -1.5 / Pirates +1.5; Over 8.5 / Under 8.5; Brandon Lowe over/under 0.5 home runs).
 * How positions group into books is decided by two per-market facts (verified live 2026-09-15,
 * docs/API-NOTES.md):
 *
 *  - multi-participant markets (`allow_multiple_participants`: player props, team totals, 3-way
 *    results) have one book per participant per line;
 *  - every other market has one book per line. A spread's two sides carry OPPOSITE signs, so they
 *    pair on the home-perspective line (away -1.5 is the same book as home +1.5); totals and
 *    yes/no markets share the number.
 *
 * Venues publish whole ladders of lines. The board shows one per market — the main line (see
 * pickMainLine) — and the venue panel lets the viewer flip through the rest.
 */
import type { Contest, FeePacket, MarketDef, PartnerQuote, Position } from "./types";

export type MarketKind = "moneyline" | "spread" | "total";
export const MARKET_KINDS: readonly MarketKind[] = ["moneyline", "spread", "total"];
export const MARKET_LABEL: Record<MarketKind, string> = {
  moneyline: "Moneyline",
  spread: "Spread",
  total: "Total",
};

export function isMainKind(key: string): key is MarketKind {
  return (MARKET_KINDS as readonly string[]).includes(key);
}

/** `listed` = the venue's quoted price. `taker` = after the venue's taker fee (its fee_packet). */
export type PriceMode = "listed" | "taker";

export interface Quote {
  partnerId: string;
  partnerName: string;
  /** The price omodds ranks and shows — listed, or fee-adjusted in taker mode. */
  price: number;
  /** The venue's own listed price, whatever the mode. */
  listedPrice: number;
  /** The API's American odds for the LISTED price; null when `price` is fee-adjusted. */
  american: string | null;
  available: number;
}

export interface SideBook {
  sideKey: string;
  /** What to call this side: the participant, or "Over" / "Under". */
  label: string;
  /** The participant's name when the data carries one (null for over/under). */
  name: string | null;
  participantId: string | null;
  imageUrl: string | null;
  /** The line from THIS side's perspective (spread +1.5, total 8.5); null when there is none. */
  line: number | null;
  /** Every venue with a fillable quote, best price first. */
  quotes: Quote[];
  /** The API positions behind this side — what price history is keyed by. */
  positionHashes?: string[];
}

export interface Book {
  /** Stable within its market across refetches — used for selection and line overrides. */
  id: string;
  marketKey: string;
  /** Set for multi-participant markets (the player, the team); null otherwise. */
  participantId: string | null;
  participantName: string | null;
  /** The book's line (home perspective for spreads); null for markets without lines. */
  line: number | null;
  sides: SideBook[];
}

/** A market with every line in it, for the full-game view. */
export interface MarketGroup {
  key: string;
  label: string;
  category: string | null;
  multi: boolean;
  hasLines: boolean;
  books: Book[];
}

/** One of the board's three markets: every quoted line, and which one is the main line. */
export interface BoardMarket {
  kind: MarketKind;
  books: Book[];
  mainId: string;
}

export interface Board {
  moneyline: BoardMarket | null;
  spread: BoardMarket | null;
  total: BoardMarket | null;
  /** Distinct venues quoting any of the three markets. */
  venueCount: number;
  /** Size available at the best price, summed over every side on the board's main lines. */
  depthUsd: number;
  /** Distinct markets on the game with at least one fillable quote. */
  marketCount: number;
  /** Markets beyond the ones the board shows — the "+N markets" chip. */
  extraMarkets: number;
}

/* ------------------------------------------------------------------ quotes */

/** Fillable = a real price AND size behind it. `price: 0, available: 0` is an empty book. */
export function isFillable(q: PartnerQuote): q is PartnerQuote & { price: number; available: number } {
  return (
    typeof q.price === "number" &&
    q.price > 0 &&
    q.price < 1 &&
    typeof q.available === "number" &&
    q.available > 0
  );
}

function effectivePrice(e: PartnerQuote & { price: number }, mode: PriceMode): number {
  if (mode === "taker") {
    const t = e.fee_packet?.taker_price;
    if (typeof t === "number" && t > 0) return t;
  }
  return e.price;
}

/**
 * One quote per venue, cheapest first. A lower probability price is a bigger payout for the same
 * outcome, so the minimum is the best price. Equal prices go to the deeper book. In taker mode
 * each venue is ranked on its fee-adjusted price, falling back to the listed price when a venue
 * reports no fee.
 */
export function rankQuotes(entries: PartnerQuote[], mode: PriceMode = "listed"): Quote[] {
  const byVenue = new Map<string, Quote>();
  for (const e of entries) {
    if (!isFillable(e)) continue;
    const price = effectivePrice(e, mode);
    if (!(price > 0 && price < 1)) continue;
    const q: Quote = {
      partnerId: e.partner_id,
      partnerName: e.partner_name ?? e.partner_id,
      price,
      listedPrice: e.price,
      american: mode === "listed" ? (e.american_odds ?? null) : null,
      available: e.available,
    };
    const prev = byVenue.get(q.partnerId);
    if (!prev || q.price < prev.price) byVenue.set(q.partnerId, q);
  }
  return [...byVenue.values()].sort((a, b) => a.price - b.price || b.available - a.available);
}

export function best(side: SideBook): Quote | null {
  return side.quotes[0] ?? null;
}

/** How many OTHER venues match the best price exactly — the "+N" badge. */
export function tiedAtBest(side: SideBook): number {
  const top = side.quotes[0];
  if (!top) return 0;
  return side.quotes.filter((q) => Math.abs(q.price - top.price) < 1e-9).length - 1;
}

/* ------------------------------------------------------------------ books */

const SIDE_ORDER: Record<string, number> = {
  away: 0,
  draw: 1,
  tie: 1,
  home: 2,
  over: 0,
  under: 1,
  yes: 0,
  no: 1,
};
function sideOrder(key: string): number {
  return SIDE_ORDER[key] ?? 9;
}
const bySide = (a: SideBook, b: SideBook) => sideOrder(a.sideKey) - sideOrder(b.sideKey);

/** Every side key across a market's books, in display order — the full-game view's columns. */
export function orderedSideKeys(books: Book[]): string[] {
  return [...new Set(books.flatMap((b) => b.sides.map((s) => s.sideKey)))].sort(
    (a, b) => sideOrder(a) - sideOrder(b),
  );
}

function toNumber(v: string | number | null | undefined): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Two decimals, sign-symmetric (quarter lines like -0.75 must survive), and never -0. */
function roundLine(n: number): number {
  const r = (Math.sign(n) * Math.round(Math.abs(n) * 100)) / 100;
  return r === 0 ? 0 : r;
}

function groupBy<T, K>(items: T[], key: (t: T) => K): Map<K, T[]> {
  const m = new Map<K, T[]>();
  for (const it of items) {
    const k = key(it);
    const arr = m.get(k);
    if (arr) arr.push(it);
    else m.set(k, [it]);
  }
  return m;
}

function sideLabel(key: string, name: string | null): string {
  if (key === "over") return "Over";
  if (key === "under") return "Under";
  if (name) return name;
  if (key === "draw" || key === "tie") return "Draw";
  return key.charAt(0).toUpperCase() + key.slice(1);
}

export function humanizeKey(key: string): string {
  return key.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Positions that are the same side of the same book merge their venues into one side. */
function mergeSide(group: Position[], line: number | null, mode: PriceMode): SideBook {
  const head = group[0];
  const key = head.side_key ?? "";
  const name = key === "over" || key === "under" ? null : (head.participant_name ?? null);
  return {
    sideKey: key,
    label: sideLabel(key, name),
    name,
    participantId: head.participant_id ?? null,
    imageUrl: head.participant_image_url ?? null,
    line,
    quotes: rankQuotes(
      group.flatMap((p) => p.partner_liquidities ?? []),
      mode,
    ),
    positionHashes: group.map((p) => p.position_hash),
  };
}

export interface MarketShape {
  /** One book per participant per line (player props, team totals, 3-way results). */
  multi: boolean;
  /** The market has lines worth showing (spreads, totals, stat lines). */
  hasLines: boolean;
}

/** Group one market's positions into books. See the module header for the rules. */
export function groupMarket(
  marketKey: string,
  positions: Position[],
  shape: MarketShape,
  mode: PriceMode = "listed",
): Book[] {
  const byBook = new Map<string, { line: number; positions: Position[] }>();
  for (const p of positions) {
    if (!p.side_key) continue;
    const v = toNumber(p.variable) ?? 0;
    // Home perspective for a single-participant market, so away -1.5 lands with home +1.5.
    const line = roundLine(!shape.multi && p.side_key === "away" ? -v : v);
    const id = shape.multi ? `${p.participant_id ?? p.participant_name ?? ""}|${line}` : String(line);
    const entry = byBook.get(id);
    if (entry) entry.positions.push(p);
    else byBook.set(id, { line, positions: [p] });
  }

  const books: Book[] = [];
  for (const [id, { line, positions: ps }] of byBook) {
    const bookLine = shape.hasLines ? line : null;
    const sides = [...groupBy(ps, (p) => p.side_key as string).entries()]
      .map(([sideKey, group]) =>
        mergeSide(
          group,
          bookLine === null ? null : !shape.multi && sideKey === "away" ? roundLine(-bookLine) : bookLine,
          mode,
        ),
      )
      .sort(bySide);
    const head = ps[0];
    books.push({
      id,
      marketKey,
      participantId: shape.multi ? (head.participant_id ?? null) : null,
      participantName: shape.multi ? (head.participant_name ?? null) : null,
      line: bookLine,
      sides,
    });
  }

  return books.sort((a, b) =>
    shape.multi
      ? (a.participantName ?? "").localeCompare(b.participantName ?? "") || (a.line ?? 0) - (b.line ?? 0)
      : (a.line ?? 0) - (b.line ?? 0),
  );
}

export function isQuoted(book: Book): boolean {
  return book.sides.some((s) => s.quotes.length > 0);
}

/**
 * Rank a candidate line — lower sorts first:
 *   1. a fillable price on BOTH sides (a one-sided book isn't a line anyone can compare),
 *   2. the most venues quoting it (the line everybody offers is the one people look for),
 *   3. the most even pair of best prices (the main line sits near 50/50),
 *   4. the smallest number, purely so the choice is deterministic.
 */
function lineScore(book: Book): number[] {
  const tops = book.sides.map((s) => s.quotes[0]);
  const complete = book.sides.length >= 2 && tops.every(Boolean) ? 0 : 1;
  const venues = new Set(book.sides.flatMap((s) => s.quotes.map((q) => q.partnerId))).size;
  const [a, b] = tops;
  const balance = a && b ? Math.abs(a.price - b.price) : 1;
  return [complete, -venues, balance, Math.abs(book.line ?? 0)];
}

function compareScores(a: number[], b: number[]): number {
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return a[i] - b[i];
  }
  return 0;
}

/** The main line among a market's books. */
export function pickMainLine(books: Book[]): Book | null {
  let chosen: Book | null = null;
  let chosenScore: number[] = [];
  for (const book of books) {
    const score = lineScore(book);
    if (!chosen || compareScores(score, chosenScore) < 0) {
      chosen = book;
      chosenScore = score;
    }
  }
  return chosen;
}

/**
 * The books a viewer can flip between from one of them: every line of a single-participant market,
 * or — for props and team totals — the same participant's other lines.
 */
export function siblingBooks(group: { multi: boolean; books: Book[] }, book: Book): Book[] {
  return group.multi ? group.books.filter((b) => b.participantId === book.participantId) : group.books;
}

/* ------------------------------------------------------------------ board */

function boardMarket(kind: MarketKind, positions: Position[], mode: PriceMode): BoardMarket | null {
  const books = groupMarket(kind, positions, { multi: false, hasLines: kind !== "moneyline" }, mode).filter(
    isQuoted,
  );
  const main = pickMainLine(books);
  if (!main || main.sides.length < 2) return null;
  return { kind, books, mainId: main.id };
}

export function buildBoard(positions: Position[], mode: PriceMode = "listed"): Board {
  const byKind = groupBy(
    positions.filter((p) => isMainKind(p.market_key ?? "")),
    (p) => p.market_key as MarketKind,
  );
  const moneyline = boardMarket("moneyline", byKind.get("moneyline") ?? [], mode);
  const spread = boardMarket("spread", byKind.get("spread") ?? [], mode);
  const total = boardMarket("total", byKind.get("total") ?? [], mode);

  const venues = new Set<string>();
  let depthUsd = 0;
  for (const market of [moneyline, spread, total]) {
    for (const side of boardBook(market)?.sides ?? []) {
      for (const q of side.quotes) venues.add(q.partnerId);
      depthUsd += side.quotes[0]?.available ?? 0;
    }
  }

  const quotedMarkets = new Set(
    positions
      .filter((p) => p.market_key && (p.partner_liquidities ?? []).some(isFillable))
      .map((p) => p.market_key as string),
  );
  const shown = [moneyline, spread, total].filter(Boolean).length;
  return {
    moneyline,
    spread,
    total,
    venueCount: venues.size,
    depthUsd,
    marketCount: quotedMarkets.size,
    extraMarkets: Math.max(0, quotedMarkets.size - shown),
  };
}

/** The book a board market shows: the viewer's chosen line if still quoted, else the main line. */
export function boardBook(market: BoardMarket | null, override?: string): Book | null {
  if (!market) return null;
  return (
    (override ? market.books.find((b) => b.id === override) : undefined) ??
    market.books.find((b) => b.id === market.mainId) ??
    null
  );
}

/**
 * "Hot" order: the most venues first — comparing prices is the whole point, so a game five
 * venues quote beats a game one venue quotes deeply — then the most size at the best prices.
 */
export function hotOrder(a: Board, b: Board): number {
  return b.venueCount - a.venueCount || b.depthUsd - a.depthUsd;
}

/* ------------------------------------------------------------------ full game */

export interface GameMarkets {
  /** Game markets: moneyline, spread, total first, then everything that isn't a player prop. */
  game: MarketGroup[];
  /** Markets the API categorises `player_stat`. */
  player: MarketGroup[];
}

function marketDefKey(m: MarketDef): string {
  return m.key ?? m.market_key ?? "";
}

/**
 * Every market on a game, grouped into books. Shape and category come from the market
 * definitions (`GET /contests/:id/markets`); a market missing from them falls back to inferring
 * shape from the positions and is filed under game markets.
 */
export function buildGameMarkets(
  positions: Position[],
  markets: MarketDef[] | undefined,
  mode: PriceMode = "listed",
): GameMarkets {
  const defs = new Map((markets ?? []).map((m) => [marketDefKey(m), m]));
  const groups: MarketGroup[] = [];
  for (const [key, ps] of groupBy(
    positions.filter((p) => p.market_key),
    (p) => p.market_key as string,
  )) {
    const def = defs.get(key);
    const multi = def?.allow_multiple_participants ?? new Set(ps.map((p) => p.participant_id)).size > 2;
    const hasLines = def?.variable_required ?? ps.some((p) => (toNumber(p.variable) ?? 0) !== 0);
    const books = groupMarket(key, ps, { multi, hasLines }, mode).filter(isQuoted);
    if (books.length === 0) continue;
    groups.push({
      key,
      label: def?.label ?? ps[0].market_label ?? humanizeKey(key),
      category: def?.category ?? null,
      multi,
      hasLines,
      books,
    });
  }
  const rank = (g: MarketGroup) => {
    const i = MARKET_KINDS.indexOf(g.key as MarketKind);
    return i === -1 ? MARKET_KINDS.length : i;
  };
  // Moneyline, spread, total first; then the busiest markets, so a tab opens on something people
  // actually trade rather than whatever sorts first alphabetically.
  groups.sort((a, b) => rank(a) - rank(b) || b.books.length - a.books.length || a.label.localeCompare(b.label));
  return {
    game: groups.filter((g) => g.category !== "player_stat"),
    player: groups.filter((g) => g.category === "player_stat"),
  };
}

/* ------------------------------------------------------------------ card layout */

export interface Teams {
  away?: string;
  home?: string;
}

function teamName(tag: unknown): string | undefined {
  if (typeof tag === "string") return tag || undefined;
  if (tag && typeof tag === "object") {
    const t = tag as { name?: unknown; abbr?: unknown };
    if (typeof t.name === "string" && t.name) return t.name;
    if (typeof t.abbr === "string" && t.abbr) return t.abbr;
  }
  return undefined;
}

export function contestTeams(contest: Contest): Teams {
  return { away: teamName(contest.tags?.away_team), home: teamName(contest.tags?.home_team) };
}

export interface CardRow {
  key: string;
  label: string;
  imageUrl: string | null;
  moneyline: SideBook | null;
  spread: SideBook | null;
  total: SideBook | null;
}

export type LineOverrides = Partial<Record<MarketKind, string>>;

/**
 * One row per team (plus a draw row where a tie is possible), with each market's side for that
 * team alongside. Totals aren't per-team, so Over goes on the first row and Under on the last —
 * the same layout every odds screen uses. `overrides` swaps in a line the viewer picked.
 */
export function cardRows(board: Board, teams: Teams = {}, overrides: LineOverrides = {}): CardRow[] {
  const ml = boardBook(board.moneyline, overrides.moneyline);
  const sp = boardBook(board.spread, overrides.spread);
  const tot = boardBook(board.total, overrides.total);
  const keys = (ml ?? sp)?.sides.map((s) => s.sideKey) ?? ["away", "home"];
  const rows = keys.map((key): CardRow => {
    const mlSide = ml?.sides.find((s) => s.sideKey === key) ?? null;
    const spSide = sp?.sides.find((s) => s.sideKey === key) ?? null;
    const fromContest = key === "away" ? teams.away : key === "home" ? teams.home : undefined;
    return {
      key,
      label: mlSide?.name ?? spSide?.name ?? fromContest ?? sideLabel(key, null),
      imageUrl: mlSide?.imageUrl ?? spSide?.imageUrl ?? null,
      moneyline: mlSide,
      spread: spSide,
      total: null,
    };
  });
  if (tot && rows.length >= 2) {
    rows[0].total = tot.sides.find((s) => s.sideKey === "over") ?? tot.sides[0] ?? null;
    rows[rows.length - 1].total = tot.sides.find((s) => s.sideKey === "under") ?? tot.sides[1] ?? null;
  }
  return rows;
}

/** "Away @ Home", in the same order as the rows. */
export function matchupTitle(rows: CardRow[], fallback?: string): string {
  const teams = rows.filter((r) => r.key !== "draw" && r.key !== "tie");
  if (teams.length === 2) return `${teams[0].label} @ ${teams[1].label}`;
  return fallback ?? teams.map((t) => t.label).join(" vs ");
}

/* ------------------------------------------------------------------ live deltas */

/** One entry of a `liquidity.update` stream frame. */
export interface LiquidityChange {
  liquidity_hash?: string;
  partner_id?: string;
  price?: number | null;
  available?: number | null;
}

/**
 * Merge stream deltas into a contest's positions by `liquidity_hash`. Returns the SAME array when
 * nothing moved (so React and the query cache see no change), plus how many deltas matched no
 * quote we hold — a new venue or line, which only a REST re-read can place.
 *
 * Deltas carry neither American odds nor a fee packet. The stale American string is dropped (it
 * re-derives from the price). The fee-adjusted prices are carried at the same ratio to the new
 * price — an approximation, since fees are not one formula across venues; the periodic REST
 * re-read replaces it with the venue's real figure.
 */
export function applyChanges(
  positions: Position[],
  changes: LiquidityChange[],
): { positions: Position[]; unmatched: number } {
  const byHash = new Map<string, LiquidityChange>();
  for (const c of changes) if (c.liquidity_hash) byHash.set(c.liquidity_hash, c);
  if (byHash.size === 0) return { positions, unmatched: 0 };

  const seen = new Set<string>();
  let changed = false;
  const next = positions.map((pos) => {
    let hit = false;
    const pls = (pos.partner_liquidities ?? []).map((pl) => {
      const c = pl.liquidity_hash ? byHash.get(pl.liquidity_hash) : undefined;
      if (!c || !pl.liquidity_hash) return pl;
      seen.add(pl.liquidity_hash);
      const price = c.price ?? null;
      const available = c.available ?? null;
      if (price === pl.price && available === pl.available) return pl;
      hit = true;

      let fee_packet = pl.fee_packet;
      const was = pl.price;
      if (fee_packet && typeof was === "number" && was > 0 && typeof price === "number" && price > 0) {
        const scale = (v: number | null | undefined) =>
          typeof v === "number" ? Math.min(0.9999, (v * price) / was) : v;
        fee_packet = {
          ...fee_packet,
          taker_price: scale(fee_packet.taker_price),
          maker_price: scale(fee_packet.maker_price),
        };
      }
      return { ...pl, price, available, american_odds: null, fee_packet };
    });
    if (!hit) return pos;
    changed = true;
    return { ...pos, partner_liquidities: pls };
  });

  return { positions: changed ? next : positions, unmatched: byHash.size - seen.size };
}

/**
 * One venue quote from the low-latency `ticks` channel (`stream:ticks`). Unlike a liquidity
 * delta it is the whole quote — American odds and fee packet included — and it names the
 * position it belongs to, so a venue that starts quoting an existing line can be placed without
 * a REST re-read.
 */
export interface Tick {
  liquidity_hash?: string;
  position_hash?: string;
  partner_id?: string;
  partner_name?: string;
  price?: number | null;
  american_odds?: string | null;
  available?: number | null;
  actionable?: boolean;
  fee_packet?: FeePacket | null;
}

/**
 * Merge ticks into a contest's positions. A tick replaces the venue's quote on that position
 * (matched by `liquidity_hash`), or is appended to the position (matched by `position_hash`) when
 * the venue wasn't quoting it yet. Returns the SAME array when nothing moved, plus how many ticks
 * matched no position — a new line, which only a REST re-read can place.
 */
export function applyTicks(positions: Position[], ticks: Tick[]): { positions: Position[]; unmatched: number } {
  const byPosition = new Map<string, Map<string, Tick>>();
  for (const t of ticks) {
    if (!t.liquidity_hash || !t.position_hash) continue;
    let m = byPosition.get(t.position_hash);
    if (!m) byPosition.set(t.position_hash, (m = new Map()));
    m.set(t.liquidity_hash, t); // a later tick in the batch wins
  }
  if (byPosition.size === 0) return { positions, unmatched: 0 };

  let placed = 0;
  let changed = false;
  const next = positions.map((pos) => {
    const mine = byPosition.get(pos.position_hash);
    if (!mine) return pos;
    placed++;
    let hit = false;
    const pending = new Map(mine);
    const pls = (pos.partner_liquidities ?? []).map((pl) => {
      const t = pl.liquidity_hash ? pending.get(pl.liquidity_hash) : undefined;
      if (!t || !pl.liquidity_hash) return pl;
      pending.delete(pl.liquidity_hash);
      const merged = mergeTick(pl, t);
      if (merged !== pl) hit = true;
      return merged;
    });
    for (const t of pending.values()) {
      if (!t.partner_id) continue;
      pls.push(mergeTick({ partner_id: t.partner_id, liquidity_hash: t.liquidity_hash }, t));
      hit = true;
    }
    if (!hit) return pos;
    changed = true;
    return { ...pos, partner_liquidities: pls };
  });

  return { positions: changed ? next : positions, unmatched: byPosition.size - placed };
}

function mergeTick(pl: PartnerQuote, t: Tick): PartnerQuote {
  const price = t.price ?? null;
  const available = t.available ?? null;
  const american_odds = t.american_odds ?? null;
  const fee_packet = t.fee_packet === undefined ? pl.fee_packet : t.fee_packet;
  if (
    price === pl.price &&
    available === pl.available &&
    american_odds === pl.american_odds &&
    sameFees(fee_packet, pl.fee_packet)
  ) {
    return pl;
  }
  return { ...pl, partner_name: t.partner_name ?? pl.partner_name, price, available, american_odds, fee_packet };
}

function sameFees(a: FeePacket | null | undefined, b: FeePacket | null | undefined): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.taker_price === b.taker_price && a.maker_price === b.maker_price;
}
